// Public vendor purchase-order view at /po/:uuid.
//
// This is the surface the "View PO" button in the vendor email
// (poVendorUrl → /po/:uuid) lands on. Before this page existed the email
// linked to a dead route (Workflow Readiness BLOCKER B2) and POs were
// permanently stuck at "sent" because vendor_acknowledged had no trigger.
//
// Anonymous-by-default — the share UUID is the gate. Staff with a valid JWT
// get a draft-stage preview pass via the backend's staffPreview flag. Layout
// + brand styling mirror PublicOrderPage (/o/:uuid).
//
// The document is vendor-grade: per line it shows the supplier SKU, product
// name + description, a reference image, the variant breakdown (size × qty ×
// unit cost), the decoration details (method, location, ink, imprint charges),
// and short-lived download links to the artwork files — everything a promo
// supplier needs to actually fulfil the order. Artwork links are minted
// server-side with a short TTL; the page never receives a permanent URL.

import { SplitShipTo, type PoSplit } from "@/components/po/SplitShipTo";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, ImageOff } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import {
  hasArtworkDetails,
  imprintSizeLabel,
  proofTypeLabel,
} from "@/lib/po-artwork";
import { useAuth } from "@/auth/AuthProvider";
import { DocumentPrintStyles } from "@/components/documents/PrintStyles";
import { OTHER_AIM_NUMBER, OTHER_ASI_NUMBER, colorCountWords, poChargeName } from "@merchd/shared";

interface PoAddressSnapshot {
  label?: string;
  companyName?: string | null;
  attention?: string;
  street1?: string;
  street2?: string;
  city?: string;
  stateCode?: string;
  postalCode?: string;
  countryCode?: string;
  [k: string]: unknown;
}

interface PublicPo {
  id: string;
  poNumber: number;
  internalStage: string;
  requiredShipDate: string | null;
  supplierShipDate: string | null;
  shippingMethod: string | null;
  inHandsDate: string | null;
  notes: string | null;
  terms: string | null;
  termsAndConditions: string | null;
  shippingAddressSnapshot: PoAddressSnapshot | null;
  // Client carrier account the vendor should bill — present ONLY when set.
  shippingAccount: {
    carrier: string;
    accountNumber: string;
    label: string | null;
    /** Billing zip — only on Other's own accounts. */
    zip?: string | null;
  } | null;
  // "Ships from X" routing (decoration flow). Null on plain product supply.
  shipFromVendorName: string | null;
  /** Our account number with this supplier (optional, from the vendor page). */
  supplierAccountNumber?: string | null;
  /** The shipping price they quoted — sent ONLY when the PO ships on their
   *  own account (Stephanie 2026-10-14). */
  shippingQuote?: string | null;
  subtotalCost: string;
  sentAt: string | null;
  vendorAcknowledgedAt: string | null;
  createdAt: string | null;
}

interface PublicPoVendor {
  name: string;
  email: string | null;
  phone: string | null;
}

interface PoArtwork {
  fileId: string;
  filename: string;
  mimeType: string;
  url: string | null;
  expiresAt: string | null;
  proofRequired: boolean;
  notes: string | null;
  /** A picture of PDF / AI / EPS art (Stephanie 2026-10-27). */
  previewUrl?: string | null;
}

interface PoDecorationCharge {
  chargeType: string;
  amount: string;
  billingModel: "fixed" | "per_piece";
  /** How many (null = default: fixed 1, per piece the line's units). */
  quantity?: number | null;
  note: string | null;
}

interface PoDecoration {
  id: string;
  location: string | null;
  imprintType: string | null;
  colorCount: number | null;
  inkColors: string[];
  artwork: PoArtwork | null;
  charges: PoDecorationCharge[];
  // Artwork Details (T16) — the structured spec the supplier decorates from.
  logoName: string | null;
  logoColor: string | null;
  proofType: string | null;
  logoWidthIn: string | null;
  logoHeightIn: string | null;
  useMaxSize: boolean;
  logoSize?: string | null;
  repeatLogo: string | null;
}

interface PoVariant {
  id: string;
  color: string | null;
  size: string | null;
  material: string | null;
  skuSuffix: string | null;
  quantity: number;
  unitCost: string | null;
}

interface PublicPoLine {
  id: string;
  lineItemId: string;
  name: string;
  serviceType: string;
  quantity: number;
  unitCost: string;
  totalCost: string | null;
  /** On a decorator's PO: items to decorate, no goods cost shown. */
  decorationOnly?: boolean;
  position: number;
  sku: string | null;
  description: string | null;
  imageUrl: string | null;
  variants: PoVariant[];
  decorations: PoDecoration[];
  artwork: PoArtwork[];
}

interface PoContact {
  name: string | null;
  email: string | null;
  phone: string | null;
}

interface PoBillTo {
  name: string;
  line1: string;
  cityLine: string;
  country: string;
}

interface PublicPoResponse {
  po: PublicPo;
  vendor: PublicPoVendor;
  sentBy: PoContact | null;
  billTo: PoBillTo;
  lines: PublicPoLine[];
  /** Split shipping: item → each address + quantities. */
  shipSplits?: Record<string, PoSplit[]>;
  staffPreview?: boolean;
}

function formatCurrency(n: number | string): string {
  const num = typeof n === "string" ? Number(n) : n;
  if (!Number.isFinite(num)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `$${num.toFixed(2)}`;
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso); // calendar day, not UTC midnight
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function labelize(s: string | null | undefined): string {
  if (!s) return "";
  return s.replace(/_/g, " ");
}

const CARRIER_LABELS: Record<string, string> = {
  fedex: "FedEx",
  ups: "UPS",
  usps: "USPS",
  dhl: "DHL",
  freight: "Freight",
  supplier_direct: "Supplier direct",
  other: "Other",
};

function carrierLabel(carrier: string | null | undefined): string {
  if (!carrier) return "";
  return CARRIER_LABELS[carrier] ?? carrier;
}

// Artwork Details (T16) display helpers — proofTypeLabel / imprintSizeLabel /
// hasArtworkDetails — live in @/lib/po-artwork, shared with the staff PO
// detail preview so the two renderings can't drift.

function variantLabel(v: PoVariant): string {
  const parts: string[] = [];
  if (v.color) parts.push(v.color);
  if (v.size) parts.push(v.size);
  if (v.material) parts.push(v.material);
  const label = parts.join(" · ");
  return label || "Standard";
}

// merch'd company block — the PO is issued by merch'd to the vendor, so the
// footer + Bill-to show merch'd's info (mirrors PublicOrderPage's COMPANY_INFO
// and the server-side MERCHD_BILL_TO constant).
const COMPANY_INFO = {
  // Same as the server's PO_COMPANY_NAME (Stephanie 2026-10-27).
  name: "Merch'd DBA other",
  line1: "3013 Yamato Rd. B12 Suite 301",
  cityLine: "Boca Raton, FL 33434",
  country: "United States",
};

/** "United States" / "US" / "USA" — not printed on a US address. */
const isUs = (c: string | null | undefined): boolean => /^(us|usa|united states( of america)?)$/i.test((c ?? "").trim());

export function PublicPOPage() {
  const { uuid = "" } = useParams<{ uuid: string }>();
  const [data, setData] = useState<PublicPoResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionState, setActionState] = useState<"idle" | "saving" | "done">(
    "idle",
  );
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  // Wait for AuthProvider to settle so a logged-in rep's staff JWT travels
  // with the data fetch (staff draft-preview pass). Anonymous vendors have
  // no token; the api helper sends no header in that case.
  const { loading: authLoading } = useAuth();

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    setLoadError(null);
    api<PublicPoResponse>(`/po/${uuid}`, { skipAuthRefresh: true })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        document.title = `PO #${res.po.poNumber} · ${COMPANY_INFO.name}`;
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setLoadError("This purchase order link is no longer active.");
        } else {
          setLoadError("Couldn't load this purchase order. Try again later.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [uuid, authLoading]);

  const lines = useMemo(() => data?.lines ?? [], [data]);

  async function acknowledge(): Promise<void> {
    if (!data) return;
    setActionState("saving");
    setActionMessage(null);
    try {
      await api(`/po/${uuid}/acknowledge`, {
        method: "POST",
        body: JSON.stringify({}),
        skipAuth: true,
      });
      setActionState("done");
      setActionMessage("Thanks — we've let the team at other know you're on it.");
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      setActionState("idle");
      setActionMessage(
        err instanceof Error ? err.message : "Couldn't submit — try again.",
      );
    }
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-cream-50 px-6 py-24 text-center font-display">
        <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Purchase order not available
        </h1>
        <p className="mt-3 text-brown-400">{loadError}</p>
        <p className="mt-8 text-sm text-brown-300">
          If you think this is a mistake, please reply to the email you
          received with the link.
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-cream-50 px-6 py-24 text-center text-brown-300">
        Loading purchase order…
      </div>
    );
  }

  if (actionState === "done") {
    return (
      <div className="min-h-screen bg-cream-50 text-brown-500 font-sans">
        <header className="fixed top-0 left-0 right-0 z-50 bg-cream-50/95 backdrop-blur-md border-b border-brown-100 print:hidden">
          <div className="mx-auto flex max-w-[1280px] items-center justify-between px-6 md:px-16 h-20">
            {/* The company's name instead of the other logo until suppliers have "other" on
                file (Stephanie 2026-11-02: "say merch'd DBA other until we have fully transitioned"). */}
            <a href="/" className="block font-display text-[22px] font-semibold tracking-[-0.4px] text-brown-500" aria-label={COMPANY_INFO.name}>
              {COMPANY_INFO.name}
            </a>
          </div>
        </header>
        <main className="pt-32 pb-20">
          <div className="mx-auto max-w-2xl px-6 md:px-16 text-center">
            <div className="rounded-3xl border border-brown-100 bg-white p-10 md:p-16">
              <h1
                className="font-display text-brown-500 mb-4"
                style={{ fontSize: "42px", lineHeight: 1.1, fontWeight: 600 }}
              >
                {actionMessage}
              </h1>
              <p
                className="text-brown-400 mb-8"
                style={{ fontSize: "16px", lineHeight: 1.65, fontWeight: 400 }}
              >
                You can close this tab. The team at other will follow up with
                the next step.
              </p>
              <a
                href="https://withother.com"
                className="inline-flex items-center justify-center rounded-xl bg-ink px-8 py-3.5 uppercase tracking-wider text-white hover:bg-ink-700 transition-colors"
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                }}
              >
                Visit withother.com
              </a>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const { po, vendor, sentBy, billTo } = data;
  // Only a PO awaiting the vendor (stage 'sent') can be acknowledged. Once
  // acknowledged it moves to 'confirmed' (or beyond), which flips the page
  // to the acknowledged-state display.
  const canAcknowledge = po.internalStage === "sent";
  const isAcknowledged =
    po.internalStage !== "draft" && po.internalStage !== "sent";

  return (
    <div className="min-h-screen bg-cream-50 text-brown-500 font-sans print:min-h-0">
      <DocumentPrintStyles />
      {/* PO PDF (Stephanie 2026-11-03: "multiple problems with the PDF download"): no
          browser date / web address in the page corners (page margin 0, our own
          padding on every page) and the page sizes below. Colours as on screen. */}
      <style>{`@media print {
        @page { margin: 0; }
        /* Everything a size smaller on paper ("everything is too large", 2026-11-03). */
        main { padding: 0 0.65in !important; zoom: 0.8; }
      }`}</style>
      <header className="fixed top-0 left-0 right-0 z-50 bg-cream-50/95 backdrop-blur-md border-b border-brown-100 print:hidden">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between px-6 md:px-16 h-20">
          {/* The company's name instead of the other logo until suppliers have "other" on
              file (Stephanie 2026-11-02: "say merch'd DBA other until we have fully transitioned"). */}
          <a href="/" className="block font-display text-[22px] font-semibold tracking-[-0.4px] text-brown-500" aria-label={COMPANY_INFO.name}>
            {COMPANY_INFO.name}
          </a>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider text-brown-400 hover:text-rust-400 transition-colors"
          >
            <Download className="h-4 w-4" /> Download as PDF
          </button>
        </div>
      </header>

      <main className="pt-32 pb-20 print:pt-0 print:pb-0">
        {/* In the PDF, a blank band repeats at the top and bottom of EVERY page (a
            table's header/footer rows repeat per printed page), so page 2+ never
            starts flush at the paper's edge. On screen these are plain blocks. */}
        <table className="block w-full print:table">
          <thead className="hidden print:table-header-group"><tr><td><div style={{ height: "0.5in" }} /></td></tr></thead>
          <tfoot className="hidden print:table-footer-group"><tr><td><div style={{ height: "0.5in" }} /></td></tr></tfoot>
          <tbody className="block print:table-row-group"><tr className="block print:table-row"><td className="block print:table-cell">
        <div className="mx-auto max-w-[1280px] px-6 md:px-16 print:px-0">
          {data.staffPreview && (
            <div className="mb-8 rounded-md border border-peach-400 bg-peach-200 px-4 py-3 text-sm text-brown-500 print:hidden">
              <strong className="font-semibold">Preview mode.</strong> You're
              viewing this purchase order as a rep — the vendor link won't
              activate until you click "Send to vendor" on the staff PO page.
            </div>
          )}

          {/* Document header */}
          <header className="mb-10 border-b border-brown-100 pb-7 print:mb-6 print:pb-5">
            {/* The page's top bar doesn't print, so the PDF says who it's from here. */}
            <p className="mb-4 hidden font-display text-[18px] font-semibold text-brown-500 print:block">{COMPANY_INFO.name}</p>
            <p
              className="mb-2.5 text-rust-400 uppercase"
              style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.14em" }}
            >
              Purchase Order
            </p>
            <h1
              className="font-display text-brown-500"
              style={{
                fontSize: "38px",
                lineHeight: 1.12,
                fontWeight: 700,
                letterSpacing: "-0.01em",
              }}
            >
              {`Purchase Order #${po.poNumber}`}
            </h1>
            <p
              className="mt-2 text-brown-400"
              style={{ fontSize: "18px", fontWeight: 600 }}
            >
              {vendor.name}
            </p>
          </header>

          {/* Acknowledged-state banner */}
          {isAcknowledged && (
            <div className="mb-8 rounded-3xl border border-brown-100 bg-white px-6 py-4 print:hidden">
              <p className="text-brown-500 font-semibold">
                Order acknowledged
              </p>
              <p className="mt-1 text-sm text-brown-400">
                {po.vendorAcknowledgedAt
                  ? `You acknowledged this order on ${formatDate(po.vendorAcknowledgedAt)}. Thank you!`
                  : "This order has been acknowledged. Thank you!"}
              </p>
            </div>
          )}

          {/* Bill-to / Ship-to / metadata */}
          <section className="mb-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4 print:mb-6 print:grid-cols-2 print:gap-5">
            <div>
              <p
                className="mb-1 text-brown-400 uppercase"
                style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.08em" }}
              >
                Bill to
              </p>
              <div className="text-brown-500 text-sm leading-snug">
                <p className="font-semibold">{billTo.name}</p>
                <p>{billTo.line1}</p>
                <p>{billTo.cityLine}</p>
                {/* Country only when it isn't the US (Stephanie 2026-10-27). */}
                {!isUs(billTo.country) && <p>{billTo.country}</p>}
              </div>
            </div>
            <div>
              <PoAddressBlock label="Ship to" address={po.shippingAddressSnapshot} />
              {Object.values(data?.shipSplits ?? {}).some((x) => x.length > 0) && (
                <p className="mt-2 text-sm font-semibold text-rust-600">
                  Some items ship to several addresses — see each item below.
                </p>
              )}
            </div>
            {/* The PO facts in one grid, three rows that line up, on the right next to Bill to / Ship to (Stephanie
                2026-10-27: "it looks cluttered"): dates, then shipping +
                payment, then our numbers. No lines between rows. */}
            <div className="space-y-5 md:col-span-2 lg:col-span-2 print:col-span-2 print:space-y-3">
              {(
                [
                  [
                    ["PO #", String(po.poNumber)],
                    ["In hands", po.inHandsDate ? formatDate(po.inHandsDate) : null],
                    ["Ship by", po.requiredShipDate ? formatDate(po.requiredShipDate) : null],
                    // No "Supplier ship" — Ship by is the date (Stephanie 2026-10-27).
                  ],
                  // ASI / AIM squeeze into the shipping row (Stephanie 2026-11-03:
                  // "the ASI and AIM number can squeeze into the row above").
                  [
                    ["Ship via", po.shippingMethod ?? null],
                    ["Terms", po.terms ? labelize(po.terms) : null],
                    ["ASI #", OTHER_ASI_NUMBER],
                    ["AIM #", OTHER_AIM_NUMBER],
                  ],
                  [
                    ["Our account #", po.supplierAccountNumber ?? null],
                  ],
                ] as Array<Array<[string, string | null]>>
              ).map((row, i) => {
                const cells = row.filter(([, v]) => v);
                if (cells.length === 0) return null;
                return (
                  <div key={i} className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-4 print:grid-cols-4">
                    {cells.map(([label, value]) => (
                      <div key={label}>
                        <p
                          className="mb-1 text-brown-400 uppercase"
                          style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.08em" }}
                        >
                          {label}
                        </p>
                        {/* Numbers exactly as typed; words capitalised. */}
                        <p className={`text-brown-500 text-sm font-semibold${label.endsWith("#") ? "" : " capitalize"}`}>{value}</p>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Shipping instructions — how + where to ship. The dates live in the
              meta grid above (In hands = client in-hands, Ship by = required
              ship date). Here we surface the carrier account to bill (ONLY when
              set — otherwise Other pays freight, and the vendor sees nothing)
              and the "ships from X" routing (decoration flow). */}
          {(po.shippingMethod ||
            po.shippingAccount ||
            po.shipFromVendorName ||
            (po.notes && po.notes.trim().length > 0)) && (
            <section className="mb-10 rounded-3xl border border-brown-100 bg-white px-6 py-4">
              <p
                className="mb-2 text-brown-400 uppercase"
                style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.08em" }}
              >
                Shipping instructions
              </p>
              <div className="space-y-1 text-brown-500" style={{ fontSize: "14px" }}>
                {po.shippingMethod && (
                  <p>
                    <span className="font-semibold">Ship via:</span>{" "}
                    {po.shippingMethod}
                  </p>
                )}
                {po.shippingAccount &&
                  po.shippingAccount.carrier === "supplier_direct" &&
                  po.shippingAccount.accountNumber === "SUPPLIER" && (
                  // "Ship on supplier's account" (2026-10-14).
                  <>
                    <p>
                      <span className="font-semibold">Freight:</span> Ship on supplier&apos;s account
                    </p>
                    {po.shippingQuote && (
                      <p>
                        <span className="font-semibold">Shipping as quoted:</span>{" "}
                        {formatCurrency(po.shippingQuote)}
                      </p>
                    )}
                  </>
                )}
                {po.shippingAccount && po.shippingAccount.carrier !== "supplier_direct" && (
                  <p>
                    <span className="font-semibold">Bill freight to:</span>{" "}
                    {/* Our own account reads "other's FedEx account #… (33434)"
                        — its label ("Other FedEx") read like a different FedEx
                        (Stephanie 2026-10-27). A client's account keeps its label. */}
                    {po.shippingAccount.zip ? "other’s " : ""}
                    {carrierLabel(po.shippingAccount.carrier)}{" "}
                    account #{po.shippingAccount.accountNumber}
                    {po.shippingAccount.zip
                      ? ` (${po.shippingAccount.zip})`
                      : po.shippingAccount.label
                        ? ` (${po.shippingAccount.label})`
                        : ""}
                  </p>
                )}
                {/* The PO's shipping notes, with the rest of the shipping info
                    (Stephanie 2026-10-27: "the shipping notes should go under
                    the shipping instructions"). Internal notes are stripped
                    server-side. */}
                {po.notes && po.notes.trim().length > 0 && (
                  <p className="whitespace-pre-wrap">
                    <span className="font-semibold">Shipping notes:</span> {po.notes}
                  </p>
                )}
                {po.shipFromVendorName && (
                  // Decorator's PO (pilot finding #2): the blanks come from the
                  // blank supplier; the decorator ships the finished order.
                  <p className="font-semibold text-rust-500">
                    Blank goods arrive from {po.shipFromVendorName}.
                  </p>
                )}
              </div>
            </section>
          )}

          {/* Line items — one vendor-grade card per line */}
          <section className="mb-10 space-y-6">
            {lines.length === 0 ? (
              <div className="rounded-3xl border border-brown-100 bg-white p-6 text-center text-brown-300">
                No line items on this purchase order yet.
              </div>
            ) : (
              lines.map((line) => <LineCard key={line.id} line={line} splits={data?.shipSplits?.[line.lineItemId]} />)
            )}
          </section>

          {/* Order total */}
          {lines.length > 0 && (
            <section className="mb-10 rounded-3xl border border-brown-100 bg-white px-6 py-5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold uppercase tracking-wide text-brown-400">
                  Order total
                </span>
                <span className="text-xl font-bold tabular-nums text-rust-500">
                  {formatCurrency(po.subtotalCost)}
                </span>
              </div>
            </section>
          )}

          {/* Terms & conditions (only when the order actually carries them) */}
          {po.termsAndConditions && po.termsAndConditions.trim().length > 0 && (
            <section className="mb-10 rounded-3xl border border-brown-100 bg-white px-6 py-4">
              <p
                className="mb-2 text-brown-400 uppercase"
                style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.08em" }}
              >
                Terms &amp; conditions
              </p>
              <p
                className="whitespace-pre-wrap text-brown-400"
                style={{ fontSize: "13px", lineHeight: 1.6 }}
              >
                {po.termsAndConditions}
              </p>
            </section>
          )}

          {/* Acknowledge — only when the PO is awaiting the vendor (stage
              'sent'). Firing it advances the PO to 'confirmed'. */}
          {canAcknowledge && (
            <section className="mb-10 print:hidden">
              {actionMessage && (
                <p className="mb-3 text-center text-sm text-rust-500">
                  {actionMessage}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => void acknowledge()}
                  disabled={actionState === "saving"}
                  className="rounded-xl bg-ink px-10 py-3 text-sm font-semibold uppercase tracking-wider text-white hover:bg-ink-700 disabled:opacity-50"
                  style={{ letterSpacing: "0.08em" }}
                >
                  {actionState === "saving" ? "Sending…" : "Acknowledge order"}
                </button>
              </div>
              <p className="mt-3 text-center text-sm text-brown-300">
                Acknowledging lets the team at other know you've received this
                order and are getting started.
              </p>
            </section>
          )}

          {/* Questions? + merch'd contact / company info */}
          <section className="mb-6 rounded-3xl border border-brown-100 bg-white px-6 py-4">
            <p
              className="mb-2 text-brown-400 uppercase"
              style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.08em" }}
            >
              Questions about this order?
            </p>
            {sentBy && (sentBy.name || sentBy.email) ? (
              <p className="text-brown-400 text-sm">
                Reach{" "}
                <span className="font-semibold text-brown-500">
                  {sentBy.name ?? "the team at other"}
                </span>
                {sentBy.email ? (
                  <>
                    {" "}
                    at{" "}
                    <a
                      href={`mailto:${sentBy.email}`}
                      className="text-ink hover:underline"
                    >
                      {sentBy.email}
                    </a>
                  </>
                ) : null}
                {sentBy.phone ? ` · ${sentBy.phone}` : ""}, or reply to the email
                you received.
              </p>
            ) : (
              <p className="text-brown-400 text-sm">
                Reply to the email you received, or reach us below.
              </p>
            )}
            <div className="my-4 border-t border-brown-50" />
            <p className="text-brown-500 font-semibold">{COMPANY_INFO.name}</p>
            <p className="text-brown-400 text-sm">{COMPANY_INFO.line1}</p>
            <p className="text-brown-400 text-sm">{COMPANY_INFO.cityLine}</p>
            {!isUs(COMPANY_INFO.country) && <p className="text-brown-400 text-sm">{COMPANY_INFO.country}</p>}
          </section>
        </div>
          </td></tr></tbody>
        </table>
      </main>
    </div>
  );
}

/** The item picture, large, over the page — click anywhere or Esc to close. */
function ImageZoom(props: { src: string; alt: string; onClose: () => void }) {
  const { onClose } = props;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={props.alt}
      onClick={onClose}
      className="fixed inset-0 z-[60] flex cursor-zoom-out items-center justify-center bg-black/70 p-6 print:hidden"
    >
      <img src={props.src} alt={props.alt} className="max-h-[90vh] max-w-[90vw] rounded-xl bg-white object-contain shadow-2xl" />
    </div>
  );
}

function LineCard(props: { line: PublicPoLine; splits?: PoSplit[] }) {
  const [zoomed, setZoomed] = useState(false);
  const { line, splits } = props;
  // Per-variant rows when the line has variants; otherwise a single
  // synthesized row from the line's own qty + unit cost.
  const rows =
    line.variants.length > 0
      ? line.variants.map((v) => ({
          id: v.id,
          label: variantLabel(v),
          qty: v.quantity,
          unit: Number(v.unitCost ?? line.unitCost),
        }))
      : [
          {
            id: `${line.id}-flat`,
            label: "Standard",
            qty: line.quantity,
            unit: Number(line.unitCost),
          },
        ];

  // On a decorator's PO the decoration IS the job, so it leads the card with a
  // clear heading; on a supplier's PO it follows the sizes (Stephanie 2026-10-14).
  const decorationSection =
    line.decorations.length > 0 ? (
      line.decorationOnly ? (
        <div className="mt-4 space-y-3 border-l-4 border-rust-500 pl-4">
          <p
            className="text-ink uppercase"
            style={{ fontSize: "13px", fontWeight: 700, letterSpacing: "0.08em" }}
          >
            Decoration instructions
          </p>
          {line.decorations.map((d) => (
            <DecorationBlock key={d.id} decoration={d} />
          ))}
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          <p
            className="text-brown-400 uppercase"
            style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.08em" }}
          >
            Decoration
          </p>
          {line.decorations.map((d) => (
            <DecorationBlock key={d.id} decoration={d} />
          ))}
        </div>
      )
    ) : null;

  return (
    <article className="rounded-3xl border border-brown-100 bg-white p-6 md:p-8 print:p-5">
      <div className="grid gap-6 md:grid-cols-[160px_1fr] print:grid-cols-[230px_1fr] print:gap-5">
        {/* Reference image */}
        <div>
          <div className="aspect-square overflow-hidden rounded-2xl border border-brown-100 bg-cream-100">
            {line.imageUrl ? (
              // Click to see it larger (Stephanie 2026-10-27).
              <button
                type="button"
                onClick={() => setZoomed(true)}
                aria-label={`See ${line.name} larger`}
                className="block h-full w-full cursor-zoom-in"
              >
                <img
                  src={line.imageUrl}
                  alt={line.name}
                  className="h-full w-full object-contain"
                />
              </button>
            ) : (
              <div className="flex h-full w-full items-center justify-center text-brown-300">
                <ImageOff className="h-6 w-6" />
              </div>
            )}
          </div>
          {zoomed && line.imageUrl && <ImageZoom src={line.imageUrl} alt={line.name} onClose={() => setZoomed(false)} />}
        </div>

        <div>
          {line.sku && (
            <p
              className="mb-1 text-rust-400 uppercase tabular-nums"
              style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.08em" }}
            >
              SKU {line.sku}
            </p>
          )}
          <h2
            className="font-display text-brown-500"
            style={{ fontSize: "20px", lineHeight: 1.2, fontWeight: 600 }}
          >
            {line.name}
          </h2>
          {line.description && line.description !== line.name && (
            <p
              className="mt-1.5 whitespace-pre-wrap text-brown-400"
              style={{ fontSize: "14px", lineHeight: 1.55 }}
            >
              {line.description}
            </p>
          )}

          {line.decorationOnly && decorationSection}

          {/* Variant breakdown */}
          <table className="mt-4 w-full text-sm">
            <thead>
              <tr className="text-left text-[13px] font-semibold uppercase tracking-wide text-brown-400">
                <th className="py-1.5">Item</th>
                <th className="py-1.5 text-right">Qty</th>
                {!line.decorationOnly && <th className="py-1.5 text-right">Unit cost</th>}
                {!line.decorationOnly && <th className="py-1.5 text-right">Amount</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-brown-50">
              {rows.map((r) => {
                const unit = r.unit;
                const amount = (Number.isFinite(unit) ? unit : 0) * r.qty;
                return (
                  <tr key={r.id}>
                    <td className="py-2 text-brown-500">{r.label}</td>
                    <td className="py-2 text-right tabular-nums text-brown-500">
                      {r.qty.toLocaleString()}
                    </td>
                    {!line.decorationOnly && (
                      <td className="py-2 text-right tabular-nums text-brown-400">
                        {formatCurrency(unit)}
                      </td>
                    )}
                    {!line.decorationOnly && (
                      <td className="py-2 text-right tabular-nums text-brown-500">
                        {formatCurrency(amount)}
                      </td>
                    )}
                  </tr>
                );
              })}
              <tr className="border-t-2 border-brown-100 font-semibold">
                <td className="py-2 text-brown-500">Line total</td>
                <td className="py-2 text-right tabular-nums text-brown-500">
                  {line.quantity.toLocaleString()}
                </td>
                {!line.decorationOnly && <td />}
                {!line.decorationOnly && (
                  <td className="py-2 text-right tabular-nums text-rust-500">
                    {formatCurrency(
                      line.totalCost
                        ? Number(line.totalCost)
                        : Number(line.unitCost) * line.quantity,
                    )}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
          <SplitShipTo splits={splits} />

          {!line.decorationOnly && decorationSection}

          {/* Line-level artwork files (superset of per-decoration art) */}
          {line.artwork.length > 0 && (
            <div className="mt-5">
              <p
                className="mb-2 text-brown-400 uppercase"
                style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.08em" }}
              >
                Artwork files
              </p>
              <div className="flex flex-wrap gap-3">
                {line.artwork.map((a) => (
                  <ArtworkThumb key={a.fileId} artwork={a} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function DecorationBlock(props: { decoration: PoDecoration }) {
  const { decoration: d } = props;
  return (
    <div className="rounded-2xl border border-brown-50 bg-cream-50 p-4">
      <div className="flex flex-wrap items-start gap-2 text-[15px] text-brown-500">
        {d.location && <span className="font-semibold">{d.location}</span>}
        {d.imprintType && (
          <span className="rounded-full bg-peach-100 px-2 py-0.5 text-sm font-semibold text-brown-500">
            {d.imprintType}
          </span>
        )}
      </div>
      <div className="mt-1 text-sm text-brown-400">
        {colorCountWords(d.colorCount) && <span>{colorCountWords(d.colorCount)}</span>}
        {d.inkColors.length > 0 && (
          <span>
            {colorCountWords(d.colorCount) ? " · " : ""}
            {d.inkColors.join(", ")}
          </span>
        )}
      </div>

      {/* Imprint / artwork charges */}
      {d.charges.length > 0 && (
        <table className="mt-3 w-full text-[15px]">
          <tbody className="divide-y divide-brown-50">
            {d.charges.map((c, i) => (
              <tr key={`${d.id}-charge-${i}`}>
                <td className="py-1.5 text-brown-500">
                  {/* Just the charge's own name — "Setup Charge", "Run Charge" —
                      never "Other — Setup" (Stephanie 2026-10-27). */}
                  {poChargeName(c)}
                  {c.billingModel === "per_piece" && (
                    <span className="ml-1 text-brown-400">/ pc</span>
                  )}
                  {c.quantity != null && (c.billingModel === "per_piece" || c.quantity !== 1) && (
                    <span className="ml-1 text-brown-400">× {c.quantity.toLocaleString()}</span>
                  )}
                </td>
                <td className="py-1.5 text-right tabular-nums text-brown-400">
                  {formatCurrency(c.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Artwork Details (T16) — the structured spec + the artwork image the
          supplier decorates from. Omitted entirely when there's nothing to show.
          The image lives INSIDE the block (R2) so the vendor never gets a spec
          without the art on the normal upload path. Location is NOT repeated
          here (R5) — it's already the decoration header above. */}
      {hasArtworkDetails(d) && (
        <div className="mt-3 rounded-xl border border-brown-50 bg-white p-3">
          <p
            className="mb-2 text-brown-400 uppercase"
            style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.08em" }}
          >
            Artwork Details
          </p>
          <dl className="grid grid-cols-[auto,1fr] items-baseline gap-x-4 gap-y-1.5 text-[15px]">
            <ArtworkDetailRow label="Logo" value={d.logoName} />
            <ArtworkDetailRow label="Size" value={imprintSizeLabel(d) || null} />
            <ArtworkDetailRow label="Logo color" value={d.logoColor} />
            <ArtworkDetailRow label="Re-order" value={d.repeatLogo} />
            <ArtworkDetailRow label="Proof" value={proofTypeLabel(d.proofType) || null} />
          </dl>
          {d.artwork && (
            <div className="mt-3">
              <ArtworkThumb artwork={d.artwork} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** One label/value row inside the Artwork Details block; renders nothing when
 *  the value is empty so the block never shows a bare label. The label (dt)
 *  matches the page's PO-facts label treatment (R4). */
function ArtworkDetailRow(props: { label: string; value: string | null }) {
  const v = props.value?.trim();
  if (!v) return null;
  return (
    <>
      <dt
        className="text-brown-400 uppercase"
        style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.08em" }}
      >
        {props.label}
      </dt>
      <dd className="font-medium text-brown-500">{v}</dd>
    </>
  );
}

function ArtworkThumb(props: { artwork: PoArtwork }) {
  const { artwork: a } = props;
  // A presigned URL can 404 after upload (expired link, deleted object) — swap
  // to the same ImageOff fallback a missing URL gets instead of letting the
  // browser render its broken-image icon.
  const [imgFailed, setImgFailed] = useState(false);
  // An image shows itself; PDF / AI / EPS art shows its drawn preview
  // (Stephanie 2026-10-27: "why are the image thumbnails broken").
  const picture = a.mimeType.startsWith("image/") ? a.url : (a.previewUrl ?? null);
  const isImage = !!picture && !imgFailed;
  const inner = (
    <div className="flex items-center gap-3 rounded-xl border border-brown-100 bg-white px-3 py-2">
      <div className="h-12 w-12 overflow-hidden rounded-lg border border-brown-50 bg-cream-100">
        {isImage ? (
          <img
            src={picture ?? undefined}
            alt={a.filename}
            loading="lazy"
            className="h-full w-full object-contain"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-brown-300">
            <ImageOff className="h-4 w-4" />
          </div>
        )}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-brown-500">
          {a.filename}
        </p>
        <p className="text-sm text-brown-300">
          {a.url ? "Download" : "Link unavailable"}
          {a.proofRequired ? " · proof required" : ""}
        </p>
      </div>
    </div>
  );
  if (a.url) {
    return (
      <a
        href={a.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex hover:opacity-90"
        download={a.filename}
      >
        {inner}
      </a>
    );
  }
  return inner;
}

function PoAddressBlock(props: {
  label: string;
  address: PoAddressSnapshot | null;
}) {
  const { label, address } = props;
  return (
    <div className="md:col-span-1 lg:col-span-1">
      <p
        className="mb-1 text-brown-400 uppercase"
        style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.08em" }}
      >
        {label}
      </p>
      {address ? (
        <div className="text-brown-500 text-sm leading-snug">
          {/* Company on top, then "Attn: <contact>" (Stephanie 2026-10-14) — a
              client address's internal name ("Main") is never shown; for a
              vendor/decorator destination the label is the vendor's name. */}
          {(() => {
            const heading = address.companyName || address.label || address.attention;
            return (
              <>
                {heading && <p className="font-semibold">{heading}</p>}
                {address.attention && address.attention !== heading && <p>Attn: {address.attention}</p>}
              </>
            );
          })()}
          {address.street1 && <p>{String(address.street1)}</p>}
          {address.street2 && <p>{String(address.street2)}</p>}
          {(address.city || address.stateCode || address.postalCode) && (
            <p>
              {[[address.city, address.stateCode].filter(Boolean).join(", "), address.postalCode]
                .filter(Boolean)
                .join(" ")}
            </p>
          )}
          {address.countryCode && !/^(US|USA)$/i.test(String(address.countryCode)) && (
            <p>{String(address.countryCode)}</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-brown-300 italic">Not provided</p>
      )}
    </div>
  );
}

